import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Col, Row, Spinner2, Text } from 'components';
import { theme } from 'lib';
import useShoutoutPresets from './useShoutoutPresets';
import { timeUntil } from './utils';

const PresetPill = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 16px;
  border-radius: 12px;
  background-color: ${({ $dark }) => ($dark ? '#fff' : theme.GREY_10)};
  border: 2px solid ${({ $selected }) => ($selected ? theme.SECONDARY : 'transparent')};
  cursor: pointer;

  & + & {
    margin-top: 8px;
  }
`;

/*
  Shown instead of the preset pills when the user's eligible presets dropped
  below 3 (per-user cooldowns) — never render a broken-looking 1-2 pill list.
  Dashed outline reads as "temporarily locked"; the frosted fill is for the
  dark (gradient) variant, the light one sits on a white modal.
*/
const LockedCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 20px 16px;
  border-radius: 12px;
  border: 1.5px dashed ${({ $dark }) => ($dark ? 'rgba(255, 255, 255, 0.45)' : theme.GREY_30)};
  background-color: ${({ $dark }) => ($dark ? 'rgba(255, 255, 255, 0.1)' : theme.GREY_10)};
  backdrop-filter: ${({ $dark }) => ($dark ? 'blur(4px)' : 'none')};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
`;

/*
  The preset pills for a team shout-out: loader while fetching, the cooldown
  card when fewer than 3 presets are eligible, otherwise the tappable pills.

  Selection lives with the caller (it is mutually exclusive with the custom
  composer, which only the caller owns), so this renders `selectedPresetId`
  and reports taps through `onSelect`.
*/
const ShoutoutPresets = ({
  presets,
  fallback,
  loading,
  getPresetText,
  selectedPresetId,
  onSelect,
  teamName,
  dark
}) => {
  const { t } = useTranslation();

  /*
    Check-back message for when presets are hidden: {time} is how long until
    a 3rd preset frees up (hours for soft cooldowns, days for the 14-day full
    ones); custom shoutouts stay available throughout.
  */
  function getLockedMessage() {
    const wait = timeUntil(fallback?.checkBackAt);

    if (!wait) {
      return t('callToAction.presetsLockedMessageNoTime', { name: teamName });
    }

    return t('callToAction.presetsLockedMessage', {
      name: teamName,
      time: t(`callToAction.${wait.unit === 'hours' ? 'timeInHours' : 'timeInDays'}`, {
        count: wait.count
      })
    });
  }

  function handleClick(preset) {
    // tapping the selected pill again just clears it
    onSelect(preset._id === selectedPresetId ? null : preset);
  }

  if (loading) {
    return (
      <Row centerAll noFlex pad="24px 0">
        <Spinner2 spinnerSize="32px" />
      </Row>
    );
  }

  if (fallback && !presets.length) {
    return (
      <LockedCard $dark={dark}>
        <Text size="28px" marg="0 0 8px">
          ⏳
        </Text>

        <Text b2 med center col={dark ? theme.PRIMARY_WHITE : theme.GREY_60}>
          <Trans
            components={{
              highlight: <Text spanned bold col={dark ? '#FFC93C' : theme.SECONDARY} />
            }}
          >
            {getLockedMessage()}
          </Trans>
        </Text>
      </LockedCard>
    );
  }

  return (
    <Col wid="100%" noFlex>
      {presets.map((preset) => (
        <PresetPill
          key={preset._id}
          $dark={dark}
          $selected={preset._id === selectedPresetId}
          onClick={() => handleClick(preset)}
        >
          <Text med>{getPresetText(preset)}</Text>
        </PresetPill>
      ))}
    </Col>
  );
};

export default ShoutoutPresets;
export { useShoutoutPresets, timeUntil };
